import type { PinyinOptions } from "./endpoints";

// Every TanStack Query key in one place. Keys are hierarchical so a prefix
// invalidates a whole family: invalidateQueries({ queryKey: qk.courses.all }).
export const qk = {
  dashboard: ["dashboard"] as const,
  languages: {
    all: ["languages"] as const,
    levels: (code: string | undefined) => ["languages", code, "levels"] as const,
  },
  staff: {
    all: ["staff"] as const,
    list: (p: object) => ["staff", "list", p] as const,
  },
  users: {
    all: ["users"] as const,
    list: (p: object) => ["users", "list", p] as const,
    detail: (id: string) => ["users", "detail", id] as const,
    progress: (id: string) => ["users", "detail", id, "progress"] as const,
    sprints: (id: string) => ["users", "detail", id, "sprints"] as const,
    mistakes: (id: string) => ["users", "detail", id, "mistakes"] as const,
    subscriptions: (id: string) => ["users", "detail", id, "subscriptions"] as const,
  },
  classrooms: {
    all: ["classrooms"] as const,
    list: (p: object) => ["classrooms", "list", p] as const,
    detail: (id: string) => ["classrooms", "detail", id] as const,
  },
  courses: {
    all: ["courses"] as const,
    list: (p: object) => ["courses", "list", p] as const,
    tree: (id: string | undefined) => ["courses", "tree", id] as const,
    // Under the tree key: any outline change invalidates the check too.
    publishCheck: (id: string) => ["courses", "tree", id, "publish-check"] as const,
  },
  courseImports: {
    all: ["course-imports"] as const,
    lists: ["course-imports", "list"] as const,
    list: (p: object) => ["course-imports", "list", p] as const,
    detail: (id: string) => ["course-imports", "detail", id] as const,
  },
  lessons: {
    detail: (id: string) => ["lessons", id] as const,
  },
  exerciseTypes: ["exercise-types"] as const,
  examFormats: ["exam-formats"] as const,
  // Generated pinyin, for previews of blank readings: the same text, mode
  // and language always get the same pinyin, so readings are cached by them.
  pinyin: {
    all: ["pinyin"] as const,
    text: (text: string, o: PinyinOptions = {}) => ["pinyin", "text", o.mode ?? "sentence", o.language ?? "", text] as const,
    batch: (texts: string[], o: PinyinOptions = {}) => ["pinyin", "batch", o.mode ?? "sentence", o.language ?? "", texts] as const,
  },
  vocabulary: {
    all: ["vocabulary"] as const,
    list: (p: object) => ["vocabulary", "list", p] as const,
    search: (language: string, q: string) => ["vocabulary", "search", language, q] as const,
    term: (language: string, term: string) => ["vocabulary", "term", language, term] as const,
    usage: (id: string) => ["vocabulary", "usage", id] as const,
  },
  // Hán Việt readings: overrides change every generated reading, so the prefix refreshes all.
  hanviet: {
    all: ["hanviet"] as const,
    chars: (p: object) => ["hanviet", "chars", p] as const,
    words: (p: object) => ["hanviet", "words", p] as const,
    preview: (texts: string[]) => ["hanviet", "preview", texts] as const,
  },
  readings: {
    all: ["readings"] as const,
    list: (p: object) => ["readings", "list", p] as const,
    detail: (id: string) => ["readings", "detail", id] as const,
  },
  attempts: {
    list: (p: object) => ["attempts", p] as const,
    lessonStats: (p: object) => ["attempts", "lesson-stats", p] as const,
    exams: (p: object) => ["attempts", "exams", p] as const,
    exam: (id: string) => ["attempts", "exams", "detail", id] as const,
  },
  notifications: {
    outbox: (p?: object) => (p ? ["notifications", "outbox", p] as const : ["notifications", "outbox"] as const),
    audience: (p: object) => ["notifications", "audience", p] as const,
    // Push automations ("Tự động"): the prefix refreshes list and details together.
    automations: ["notifications", "automations"] as const,
    automation: (id: string) => ["notifications", "automations", "detail", id] as const,
    automationPreview: (body: object) => ["notifications", "automations", "preview", body] as const,
    automationTriggers: ["notifications", "automation-triggers"] as const,
  },
  audit: {
    list: (p: object) => ["audit", p] as const,
  },
  essays: {
    all: ["essays"] as const,
    pending: (p: object) => ["essays", "pending", p] as const,
  },
  feedback: {
    all: ["feedback"] as const,
    list: (p: object) => ["feedback", "list", p] as const,
  },
  analytics: {
    all: ["analytics"] as const,
    summary: (p: object) => ["analytics", "summary", p] as const,
    events: (p: object) => ["analytics", "events", p] as const,
  },
  ai: {
    all: ["ai"] as const,
    settings: ["ai", "settings"] as const,
    providers: ["ai", "providers"] as const,
    models: (providerId: string | null | undefined) => ["ai", "providers", providerId, "models"] as const,
    prices: ["ai", "prices"] as const,
    features: ["ai", "features"] as const,
    usage: (p: object) => ["ai", "usage", p] as const,
  },
  promos: {
    all: ["promos"] as const,
  },
  // AI group E (speaking)
  aiRoleplays: {
    all: ["ai-roleplays"] as const,
    list: (p: object) => ["ai-roleplays", "list", p] as const,
  },
  // Language centers (docs/b2b-centers-contract.md)
  orgs: {
    all: ["orgs"] as const,
    list: (p: object) => ["orgs", "list", p] as const,
    detail: (id: string) => ["orgs", "detail", id] as const,
    tiers: ["orgs", "tiers"] as const,
  },
  center: {
    all: ["center"] as const,
    orgs: ["center", "orgs"] as const,
    org: (id: string) => ["center", id] as const,
    part: (id: string, part: string, p?: object) => ["center", id, part, p ?? {}] as const,
  },
};
